import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Upload, FileText, Trash2, Lock, Loader2, PlayCircle,
  BookOpen, Sparkles, BarChart3, Download, Mail, Eye, Send, X,
  KeyRound, CheckCircle2, MessageSquarePlus, StickyNote,
} from "lucide-react";
import AppNav from "@/components/AppNav";
import PicturePinInput, { PIN_ICON_MAP } from "@/components/PicturePinInput";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function StudentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [student, setStudent] = useState(null);
  const [ieps, setIeps] = useState([]);
  const [notes, setNotes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {subject, html, ai_note}
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [pinModal, setPinModal] = useState(false);
  const [pinStage, setPinStage] = useState("first"); // first | confirm
  const [firstPin, setFirstPin] = useState([]);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinErr, setPinErr] = useState("");
  const [noteModal, setNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, i, n] = await Promise.all([
        api.get(`/students/${id}`),
        api.get(`/students/${id}/iep`),
        api.get(`/students/${id}/notes`),
      ]);
      setStudent(s.data);
      setIeps(i.data);
      setNotes(n.data);
    } catch {
      toast.error("Could not load student.");
      nav("/dashboard");
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  // Deep link from digest email: /students/:id?add-note=1 auto-opens the note modal
  useEffect(() => {
    if (searchParams.get("add-note") === "1") {
      setNoteModal(true);
      searchParams.delete("add-note");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/students/${id}/iep`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("IEP uploaded and parsed.");
      fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const deleteIep = async (docId) => {
    if (!window.confirm("Delete this IEP file? This cannot be undone.")) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/iep/${docId}`);
      toast.success("IEP deleted.");
      await load();
    } catch {
      toast.error("Could not delete file.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/iep/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "iep.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    }
  };

  const deleteStudent = async () => {
    if (!window.confirm(`Delete ${student.name}? All lessons and progress will be removed.`)) return;
    try {
      await api.delete(`/students/${id}`);
      toast.success("Student deleted.");
      nav("/dashboard");
    } catch {
      toast.error("Could not delete student.");
    }
  };

  const openPreview = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.get(`/digest/${id}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not build the digest.");
    } finally {
      setPreviewBusy(false);
    }
  };

  const sendDigest = async () => {
    setSendBusy(true);
    try {
      const { data } = await api.post(`/digest/${id}/send`);
      toast.success(`Digest sent to ${data.sent_to}.`);
      setPreview(null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) toast.info("Email is not turned on yet. Add a Resend API key to enable weekly digests.");
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the digest.");
    } finally {
      setSendBusy(false);
    }
  };

  const openPinModal = () => {
    setPinStage("first");
    setFirstPin([]);
    setPinErr("");
    setPinModal(true);
  };

  const onPinFirst = (pin) => {
    setFirstPin(pin);
    setPinStage("confirm");
    setPinErr("");
  };

  const onPinConfirm = async (pin) => {
    if (pin.join("-") !== firstPin.join("-")) {
      setPinErr("Those two didn't match. Let's try again from the start.");
      setPinStage("first");
      setFirstPin([]);
      return;
    }
    setPinBusy(true);
    try {
      await api.post(`/students/${id}/pin`, { pin });
      toast.success("Picture PIN saved.");
      setPinModal(false);
      await load();
    } catch (e) {
      setPinErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not save the PIN.");
    } finally {
      setPinBusy(false);
    }
  };

  const clearPin = async () => {
    if (!window.confirm("Remove the picture PIN? Your child won't be able to sign in on their own until you set a new one.")) return;
    try {
      await api.delete(`/students/${id}/pin`);
      toast.success("Picture PIN removed.");
      await load();
    } catch {
      toast.error("Could not remove the PIN.");
    }
  };

  const openNoteModal = () => {
    setNoteText("");
    setNoteModal(true);
  };

  const saveNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setNoteBusy(true);
    try {
      await api.post(`/students/${id}/notes`, { text });
      toast.success("Note saved. It will guide the next homework session.");
      setNoteModal(false);
      setNoteText("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save note.");
    } finally {
      setNoteBusy(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/notes/${noteId}`);
      await load();
    } catch {
      toast.error("Could not delete note.");
    }
  };

  if (!student) {
    return (
      <>
        <AppNav />
        <main className="mx-auto max-w-6xl p-6" data-testid="student-detail-loading">
          <div className="tm-card flex items-center gap-3"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        </main>
      </>
    );
  }

  const summary = student.iep_summary;

  return (
    <>
      <AppNav />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="student-detail-page">
        <div className="flex items-center gap-4 mb-8">
          <div
            className="rounded-full grid place-items-center text-white font-black text-2xl"
            style={{
              width: 72, height: 72, background: student.avatar_color || "var(--tm-brand)",
              fontFamily: "Nunito, sans-serif",
            }}
            aria-hidden
          >
            {student.name?.[0]?.toUpperCase() || "S"}
          </div>
          <div className="flex-1">
            <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito, sans-serif" }}>
              {student.name}
            </h1>
            <p className="text-[color:var(--tm-ink-3)]">
              Age {student.age} · Reading level: {student.reading_level || "—"}
            </p>
          </div>
          <button onClick={deleteStudent} className="tm-btn tm-btn-outline" data-testid="delete-student-btn">
            <Trash2 size={16} /> <span className="hidden sm:inline">Delete</span>
          </button>
        </div>

        {/* Parent digest card */}
        <section className="tm-card-soft mb-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="digest-section">
          <div
            className="p-3 rounded-xl flex-shrink-0"
            style={{ background: "#ffffff", color: "var(--tm-brand)", border: "2px solid var(--tm-border)" }}
          >
            <Mail size={22} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>
              Send this week's digest
            </h3>
            <p className="text-[color:var(--tm-ink-3)]">
              Get a warm, AI-written summary of {student.name}'s week — streak, badges, lessons and subjects.
              Auto-sends on your chosen day when Weekly digest is on.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={openPreview}
              disabled={previewBusy}
              className="tm-btn tm-btn-outline"
              data-testid="digest-preview-btn"
            >
              {previewBusy ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
              Preview
            </button>
            <button
              onClick={sendDigest}
              disabled={sendBusy}
              className="tm-btn tm-btn-primary"
              data-testid="digest-send-btn"
            >
              {sendBusy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Send digest
            </button>
          </div>
        </section>

        {/* Parent notes card */}
        <section className="tm-card-soft mb-8" data-testid="notes-section">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div
              className="p-3 rounded-xl flex-shrink-0"
              style={{ background: "#ffffff", color: "var(--tm-brand)", border: "2px solid var(--tm-border)" }}
            >
              <StickyNote size={22} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>
                Notes for the next homework session
              </h3>
              <p className="text-[color:var(--tm-ink-3)]">
                Anything you add here is quietly woven into the AI's next reply — great for "let's practice fractions"
                or "she is worried about a spelling test on Friday."
              </p>
            </div>
            <button onClick={openNoteModal} className="tm-btn tm-btn-primary" data-testid="note-add-btn">
              <MessageSquarePlus size={16} /> Add a note
            </button>
          </div>

          {notes.length > 0 && (
            <ul className="mt-5 pt-5 border-t-2 space-y-3" style={{ borderColor: "var(--tm-border)" }} data-testid="notes-list">
              {notes.map((n) => {
                const consumed = !!n.consumed_at;
                return (
                  <li
                    key={n.id}
                    className="tm-card flex items-start gap-3"
                    data-testid={`note-item-${n.id}`}
                    style={consumed ? { opacity: 0.7 } : undefined}
                  >
                    <div
                      className="p-2 rounded-lg flex-shrink-0"
                      style={{
                        background: consumed ? "var(--tm-bg-3)" : "#ffffff",
                        border: "2px solid var(--tm-border)",
                        color: consumed ? "var(--tm-ink-3)" : "var(--tm-brand)",
                      }}
                    >
                      {consumed ? <CheckCircle2 size={18} /> : <StickyNote size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="whitespace-pre-wrap">{n.text}</p>
                      <p className="text-xs text-[color:var(--tm-ink-3)] mt-1">
                        {n.source === "digest_reply" ? "From your digest reply · " : ""}
                        {new Date(n.created_at).toLocaleDateString()}
                        {consumed ? " · used in a homework session" : " · will be used next time"}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteNote(n.id)}
                      className="tm-btn tm-btn-ghost"
                      data-testid={`note-delete-${n.id}`}
                      aria-label="Delete note"
                    >
                      <Trash2 size={16} style={{ color: "var(--tm-error)" }} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Picture PIN card */}
        <section className="tm-card-soft mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="pin-section">
          <div
            className="p-3 rounded-xl flex-shrink-0"
            style={{ background: "#ffffff", color: "var(--tm-brand)", border: "2px solid var(--tm-border)" }}
          >
            <KeyRound size={22} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>
              {student.has_pin ? "Picture PIN is set" : "Set a picture PIN"}
            </h3>
            <p className="text-[color:var(--tm-ink-3)]">
              {student.has_pin
                ? `${student.name} can sign in on their own by tapping 3 pictures.`
                : "Let your child sign in without sharing your password. Pick 3 pictures they'll tap in order."}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={openPinModal}
              className="tm-btn tm-btn-primary"
              data-testid="pin-setup-btn"
            >
              {student.has_pin ? "Change PIN" : "Set PIN"}
            </button>
            {student.has_pin && (
              <button
                onClick={clearPin}
                className="tm-btn tm-btn-outline"
                data-testid="pin-clear-btn"
              >
                Remove
              </button>
            )}
          </div>
        </section>

        {/* Quick actions for student */}
        <section className="grid md:grid-cols-3 gap-5 mb-10" data-testid="student-quick-actions">
          <Link to={`/students/${id}/homework`} className="tm-card block hover:border-[color:var(--tm-brand)] transition-colors" data-testid="quick-homework">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-xl" style={{ background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}>
                <BookOpen size={22} />
              </div>
              <h2 className="text-xl font-bold">Homework</h2>
            </div>
            <p className="text-[color:var(--tm-ink-3)]">Get help with an assignment, one step at a time.</p>
          </Link>
          <Link to={`/students/${id}/learn`} className="tm-card block hover:border-[color:var(--tm-brand)] transition-colors" data-testid="quick-learn">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-xl" style={{ background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}>
                <Sparkles size={22} />
              </div>
              <h2 className="text-xl font-bold">Learn</h2>
            </div>
            <p className="text-[color:var(--tm-ink-3)]">Bite-sized lessons about things they love.</p>
          </Link>
          <Link to={`/students/${id}/progress`} className="tm-card block hover:border-[color:var(--tm-brand)] transition-colors" data-testid="quick-progress">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-xl" style={{ background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}>
                <BarChart3 size={22} />
              </div>
              <h2 className="text-xl font-bold">Progress</h2>
            </div>
            <p className="text-[color:var(--tm-ink-3)]">See streaks, badges, and subject growth.</p>
          </Link>
        </section>

        {/* IEP section */}
        <section className="tm-card mb-8" data-testid="iep-section">
          <div className="flex items-center gap-3 mb-4">
            <Lock size={20} style={{ color: "var(--tm-brand)" }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>IEP documents</h2>
          </div>
          <p className="text-[color:var(--tm-ink-3)] mb-4">
            Files are encrypted with AES-256 before storage. We use the parsed summary
            to personalize every AI reply.
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              onChange={onUpload}
              className="hidden"
              data-testid="iep-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="tm-btn tm-btn-primary"
              disabled={uploading}
              data-testid="iep-upload-btn"
            >
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
              {uploading ? "Uploading & parsing…" : "Upload IEP (PDF)"}
            </button>
          </div>

          {ieps.length === 0 ? (
            <p className="text-[color:var(--tm-ink-3)]">No IEP files uploaded yet.</p>
          ) : (
            <ul className="space-y-3" data-testid="iep-list">
              {ieps.map((d) => (
                <li key={d.id} className="tm-card-soft flex items-center gap-3">
                  <FileText size={22} style={{ color: "var(--tm-brand)" }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{d.original_filename}</p>
                    <p className="text-sm text-[color:var(--tm-ink-3)]">
                      {(d.size / 1024).toFixed(1)} KB · uploaded {new Date(d.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button onClick={() => download(d)} className="tm-btn tm-btn-ghost" data-testid={`iep-download-${d.id}`}>
                    <Download size={16} />
                  </button>
                  <button onClick={() => deleteIep(d.id)} className="tm-btn tm-btn-ghost" disabled={deleteBusy} data-testid={`iep-delete-${d.id}`}>
                    <Trash2 size={16} style={{ color: "var(--tm-error)" }} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {summary && (Object.values(summary).some((v) => (Array.isArray(v) ? v.length : v))) && (
            <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="iep-summary">
              {summary.reading_level && (
                <SummaryBox title="Reading level" items={[summary.reading_level]} />
              )}
              {summary.accommodations?.length > 0 && (
                <SummaryBox title="Accommodations" items={summary.accommodations} />
              )}
              {summary.learning_goals?.length > 0 && (
                <SummaryBox title="Learning goals" items={summary.learning_goals} />
              )}
              {summary.sensory_notes?.length > 0 && (
                <SummaryBox title="Sensory notes" items={summary.sensory_notes} />
              )}
            </div>
          )}
        </section>

        <Link to={`/students/${id}/home`} className="tm-btn tm-btn-primary" data-testid="open-student-mode-btn">
          <PlayCircle size={18} /> Open student mode
        </Link>
      </main>

      {/* Digest preview modal */}
      {preview && (        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center p-4"
          style={{ background: "rgba(26, 32, 44, 0.55)" }}
          data-testid="digest-preview-modal"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white rounded-2xl border-2 w-full max-w-3xl max-h-[90vh] flex flex-col"
            style={{ borderColor: "var(--tm-border)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="p-4 border-b-2 flex items-center justify-between gap-3"
              style={{ borderColor: "var(--tm-border)" }}
            >
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-[color:var(--tm-ink-3)] font-bold">Preview</p>
                <p className="text-lg font-bold truncate" style={{ fontFamily: "Nunito, sans-serif" }}>
                  {preview.subject}
                </p>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="tm-btn tm-btn-ghost"
                data-testid="digest-preview-close"
                aria-label="Close preview"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-2 overflow-auto flex-1" data-testid="digest-preview-iframe-wrap">
              <iframe
                title="Digest preview"
                srcDoc={preview.html}
                style={{ width: "100%", minHeight: 520, border: 0, borderRadius: 12 }}
              />
            </div>
            <div
              className="p-4 border-t-2 flex justify-end gap-2"
              style={{ borderColor: "var(--tm-border)" }}
            >
              <button
                onClick={() => setPreview(null)}
                className="tm-btn tm-btn-outline"
                data-testid="digest-preview-cancel"
              >
                Close
              </button>
              <button
                onClick={sendDigest}
                disabled={sendBusy}
                className="tm-btn tm-btn-primary"
                data-testid="digest-preview-send"
              >
                {sendBusy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Send to my email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Picture PIN setup modal */}
      {pinModal && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center p-4"
          style={{ background: "rgba(26, 32, 44, 0.55)" }}
          data-testid="pin-setup-modal"
          onClick={() => !pinBusy && setPinModal(false)}
        >
          <div
            className="bg-white rounded-2xl border-2 w-full max-w-lg max-h-[90vh] overflow-auto"
            style={{ borderColor: "var(--tm-border)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="p-4 border-b-2 flex items-center justify-between gap-3"
              style={{ borderColor: "var(--tm-border)" }}
            >
              <div>
                <p className="text-xs uppercase tracking-wider text-[color:var(--tm-ink-3)] font-bold">
                  {pinStage === "first" ? "Step 1 of 2" : "Step 2 of 2"}
                </p>
                <p className="text-lg font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>
                  {pinStage === "first" ? "Pick 3 pictures" : "Pick the same 3 again"}
                </p>
              </div>
              <button
                onClick={() => setPinModal(false)}
                className="tm-btn tm-btn-ghost"
                data-testid="pin-modal-close"
                disabled={pinBusy}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-[color:var(--tm-ink-3)] mb-4">
                {pinStage === "first"
                  ? `Choose 3 pictures in a special order. ${student.name} will tap these to sign in.`
                  : "Tap the same 3 pictures in the same order to confirm."}
              </p>
              {pinStage === "first" ? (
                <PicturePinInput
                  key="first"
                  onComplete={onPinFirst}
                  disabled={pinBusy}
                  testIdPrefix="pin-first"
                />
              ) : (
                <>
                  <div className="tm-card-soft mb-4 flex items-center gap-3" data-testid="pin-first-summary">
                    <span className="text-sm font-bold text-[color:var(--tm-ink-3)]">You picked:</span>
                    <div className="flex gap-2">
                      {firstPin.map((k, i) => {
                        const meta = PIN_ICON_MAP[k];
                        const Icon = meta?.Icon;
                        return (
                          <span
                            key={`pin-summary-${i}-${k}`}
                            className="grid place-items-center rounded-lg"
                            style={{
                              width: 36, height: 36,
                              background: "#ffffff",
                              border: "2px solid var(--tm-border)",
                              color: meta?.color,
                            }}
                            aria-hidden
                          >
                            {Icon ? <Icon size={22} /> : null}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <PicturePinInput
                    key="confirm"
                    onComplete={onPinConfirm}
                    disabled={pinBusy}
                    testIdPrefix="pin-confirm"
                  />
                </>
              )}
              {pinBusy && (
                <div className="mt-4 flex items-center gap-2 text-[color:var(--tm-ink-3)]">
                  <Loader2 size={16} className="animate-spin" /> Saving…
                </div>
              )}
              {pinErr && (
                <p className="text-sm font-medium mt-3" style={{ color: "var(--tm-error)" }} data-testid="pin-error">
                  {pinErr}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add-a-note modal */}
      {noteModal && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center p-4"
          style={{ background: "rgba(26, 32, 44, 0.55)" }}
          data-testid="note-modal"
          onClick={() => !noteBusy && setNoteModal(false)}
        >
          <div
            className="bg-white rounded-2xl border-2 w-full max-w-lg"
            style={{ borderColor: "var(--tm-border)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="p-4 border-b-2 flex items-center justify-between gap-3"
              style={{ borderColor: "var(--tm-border)" }}
            >
              <div>
                <p className="text-xs uppercase tracking-wider text-[color:var(--tm-ink-3)] font-bold">
                  Note for {student.name}
                </p>
                <p className="text-lg font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>
                  Guide the next homework session
                </p>
              </div>
              <button
                onClick={() => setNoteModal(false)}
                className="tm-btn tm-btn-ghost"
                disabled={noteBusy}
                aria-label="Close"
                data-testid="note-modal-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <label htmlFor="note-text" className="tm-label">Your note</label>
              <textarea
                id="note-text"
                className="tm-input resize-none"
                rows={4}
                placeholder="e.g. Focus on subtraction across zero — she skipped it in class today."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                maxLength={1000}
                data-testid="note-textarea"
              />
              <p className="text-sm text-[color:var(--tm-ink-3)] mt-2">
                We fold this into the AI's system prompt for the very next homework session and mark it used afterward.
                Nothing is shown verbatim to your child.
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setNoteModal(false)} className="tm-btn tm-btn-outline" disabled={noteBusy}>
                  Cancel
                </button>
                <button
                  onClick={saveNote}
                  disabled={noteBusy || !noteText.trim()}
                  className="tm-btn tm-btn-primary"
                  data-testid="note-save-btn"
                >
                  {noteBusy ? <Loader2 size={16} className="animate-spin" /> : <MessageSquarePlus size={16} />}
                  Save note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SummaryBox({ title, items }) {
  return (
    <div className="tm-card-soft">
      <p className="text-xs font-bold uppercase tracking-wider mb-2 text-[color:var(--tm-ink-3)]">{title}</p>
      <ul className="space-y-1 text-[color:var(--tm-ink-2)]">
        {items.map((it, i) => (
          <li key={`${title}-${i}-${it.slice(0, 12)}`} className="leading-relaxed">• {it}</li>
        ))}
      </ul>
    </div>
  );
}
